import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { Router } from '@angular/router';
import { Subject, of, from } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { CommonPage } from '../../common-page/common-page';
import { ScenesService } from '../../services/scenes.service';
import { MqttConnectorService } from '../../../../mqtt/services/mqtt-connector.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { AuthService } from '../../../auth/auth.service';
import { SceneDef, buildScenePayload } from '../../interfaces/scenes.interface';
import { GridBox, GridMetrics, TileRect, clampOffsetToGrid, displacementFor, gridMetrics, targetIndexFromPoint } from './scene-grid-drag';

@Component({
  selector: 'scenes-widget',
  templateUrl: './scenes-widget.component.html',
  styleUrls: ['./scenes-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScenesWidgetComponent extends CommonPage implements OnInit, OnDestroy {
  scenes: SceneDef[] = [];
  // Collapsed view is paginated: PAGE_SIZE tiles per page (each tile is a scene, or the
  // trailing "New" tile), stepped by the bottom carets. `page` is the current page index.
  pages: Array<{ scene?: SceneDef }>[] = [];
  page = 0;
  // Reorder (edit) mode: drag a tile to a new slot; the rest slide aside to open the gap.
  editMode = false;
  private reorderDirty = false;   // a reorder happened this edit session → persist once on Done
  private orderWriteInFlight = false;   // a persist is awaiting its round trip; don't start a second
  private orderRevision = 0;            // bumped on every local move, so a slow write can tell
                                        // whether the order it carried is still the current one
  // Live-drag swipe state (the finger drags the track; it snaps to a page on release).
  dragDx = 0;
  swiping = false;
  private swipeActive = false;
  private swipeStartX = 0;
  private swipeStartY = 0;
  private swiped = false;                 // swallow the tile click that ends a drag
  // The swipe is single-pointer, like the reorder drag below: the first finger down claims the
  // carousel and every other pointer is ignored until it releases — see onSwipeStart.
  private swipePointerId: number | null = null;
  private readonly SWIPE_COMMIT_PX = 50;  // horizontal travel needed to change page
  private readonly PAGE_SIZE = 4;
  bouncingId: string | null = null;
  longPressed = false;
  controlAvailability: 'ready' | 'reconnecting' | 'needs_reauth' = 'reconnecting';
  private hasSyncedWithUnit = false;
  // Resolved once on init (never awaited in the tap handler) so a scene tap can fire
  // synchronously. Empty until the lookup resolves; a tap in that narrow window fires with
  // cognito_sub: '', which the unit will not recognise — the fallback covers it.
  private cognitoSub = '';

  // --- Drag-to-reorder state (expanded grid, reorder mode only) ---
  @ViewChild('sceneGrid') sceneGrid?: ElementRef<HTMLElement>;
  dragId: string | null = null;         // the scene currently held; null = no drag in progress
  dragTo = -1;                          // slot it is hovering over
  private dragFrom = -1;                // slot it started in
  private dragMetrics: GridMetrics | null = null;   // grid shape, measured once at drag start
  private dragBox: GridBox | null = null;           // the box the held tile is confined to
  private dragOffsetX = 0;              // pointer travel since the drag began, clamped to the grid
  private dragOffsetY = 0;
  // A pointerdown in reorder mode is only a CANDIDATE — it becomes a drag once the finger has
  // travelled far enough, so a stationary tap stays a tap.
  private dragCandidate: { id: string; index: number; x: number; y: number; el: HTMLElement } | null = null;
  // The gesture is single-pointer: the first pointer down claims the widget and every other pointer
  // is ignored until it releases. Without this a second finger hijacked the drag — see onDragStart.
  private dragPointerId: number | null = null;
  // pointermove is attached to the pressed tile for the life of the gesture, NOT bound in the
  // template: an in-zone template listener runs a change-detection pass per event even when its
  // handler returns immediately, so binding it on every tile taxed every pointer move over the grid
  // — including plain scrolling, with reorder mode off.
  private dragMoveListener: ((ev: PointerEvent) => void) | null = null;
  private readonly DRAG_START_PX = 6;
  // When a drag ends the browser may still synthesise a click (it does whenever the finger stayed
  // inside its tap slop). That click must never fire the scene, and the editMode guard in onCardTap
  // cannot be relied on for it: leaving reorder mode is one of the ways a drag ends.
  private dragEndedAt = 0;
  private readonly TAP_AFTER_DRAG_MS = 400;

  private readonly widgetDestroy$ = new Subject<void>();
  private destroyed = false;
  private pressTimer: any = null;
  private readonly LONG_PRESS_MS = 500;
  // Keep only one scene toast on screen at a time, matching the Lighting widget's circadian
  // toast. Holds the create() promise so a new toast can dismiss the previous one even before
  // the previous create() has resolved.
  private activeSceneToast?: Promise<HTMLIonToastElement>;
  // Collapse accidental double-taps so one tap = one apply command (spec: ~300ms).
  private lastActivateAt = 0;
  private readonly ACTIVATE_DEBOUNCE_MS = 300;
  // Shorter than requestApiCommand's default 10s: a scene tap that times out at 10s both
  // double-applies (once inline, once whenever the slow unit finally executes the id-based
  // command) and leaves the resident staring at a stale toast for 10 seconds.
  private readonly APPLY_SCENE_TIMEOUT_MS = 3000;
  private static readonly UNKNOWN_SCENE_ERROR_PREFIX = 'Unknown scene_id:';

  constructor(
    toast: ToastController, modalCtrl: ModalController,
    private scenesService: ScenesService,
    private mqtt: MqttConnectorService,
    private analytics: AnalyticsService,
    private auth: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) { super(toast, modalCtrl); }

  ngOnInit(): void {
    // Resolved once, up front: activate() fires synchronously and must not await this.
    this.auth.getValidCognitoUserId().then(sub => { this.cognitoSub = sub; }).catch(() => undefined);
    this.scenesService.getScenes().pipe(takeUntil(this.widgetDestroy$))
      .subscribe(scenes => {
        // While reordering — and until that order is actually saved — the local array is
        // authoritative. residentSettings$ is shared, so any other write in the app (lighting,
        // climate, onboarding) emits here; without the second guard one of those would replace a
        // pending order with the server's, and the teardown retry would then write THAT back.
        if (this.editMode || this.reorderDirty) { return; }
        this.scenes = scenes; this.rebuildPages(); this.cdr.markForCheck();
      });
    this.mqtt.getControlAvailability().pipe(takeUntil(this.widgetDestroy$))
      .subscribe(a => {
        this.controlAvailability = a;
        if (a === 'ready' && !this.hasSyncedWithUnit) {
          this.hasSyncedWithUnit = true;
          // Fire and forget: sync must never gate rendering or firing scenes.
          this.scenesService.syncWithUnit().catch(() => undefined);
        }
        this.cdr.markForCheck();
      });
  }

  /** Tap = fire the scene, unless a swipe, a long-press or a drag just happened (swallow that click). */
  onCardTap(scene: SceneDef): void {
    if (this.editMode) { return; }                           // reorder mode: tiles drag, they don't fire
    if (this.swiped) { this.swiped = false; return; }
    if (this.longPressed) { this.longPressed = false; return; }
    // The click that trails a just-finished drag. Time-boxed rather than a flag, so a drag long
    // enough that no click follows can't leave a latch behind to swallow the next genuine tap.
    if (Date.now() - this.dragEndedAt < this.TAP_AFTER_DRAG_MS) { return; }
    this.activate(scene);
  }

  activate(scene: SceneDef): void {
    const now = Date.now();
    if (now - this.lastActivateAt < this.ACTIVATE_DEBOUNCE_MS) { return; }   // swallow accidental double-taps
    this.lastActivateAt = now;
    if (!this.ensureReady()) { return; }
    this.bouncingId = scene.id; this.cdr.markForCheck();
    this.analytics.trackWidgetInteraction('scenes', 'activate', {
      sceneId: scene.id, isDefault: scene.isDefault, actionTypes: scene.actions.map(a => a.type),
    });
    from(this.mqtt.requestApiCommand('apply_resident_scene', {
      cognito_sub: this.cognitoSub,
      scene_id: scene.id,
    }, this.APPLY_SCENE_TIMEOUT_MS)).pipe(
      take(1),
      catchError((err: unknown) => {
        // Only fall back when the unit has told us it explicitly doesn't know this scene id
        // (a stale local cache that never got pushed) — firing inline there is known-safe.
        // A timeout, or any other error, is a plain failure: the unit may just be slow, and
        // re-firing risks applying the scene twice once the original command lands.
        if (!this.isUnknownSceneError(err)) { return of(null); }
        this.scenesService.pushToUnit().catch(() => undefined);
        return this.mqtt.triggerScript('apply_scene', buildScenePayload(scene)).pipe(
          take(1), catchError(() => of(null)));
      }),
    )
      .subscribe(res => {
        if (res === null) { this.presentSceneToast("Couldn't reach your home. Try again in a moment."); }
        else { this.presentSceneToast(`${scene.name} applied`); }
      });
  }

  private isUnknownSceneError(err: unknown): boolean {
    const message = err instanceof Error ? err.message : '';
    return message.startsWith(ScenesWidgetComponent.UNKNOWN_SCENE_ERROR_PREFIX);
  }

  // Styled + timed like the Lighting widget's circadian toast (same compact pill via
  // scene-toast, 1s, bottom — scene-toast wraps instead of clipping long scene names), and
  // single-instance: claim the slot synchronously and dismiss the previous toast so rapid
  // scene taps animate out the old one instead of stacking.
  private presentSceneToast(message: string): void {
    const previous = this.activeSceneToast;
    const toastPromise = this.toastController.create({
      message,
      duration: 1000,
      position: 'bottom',
      cssClass: 'scene-toast',
    });
    this.activeSceneToast = toastPromise;

    previous?.then((toast) => toast.dismiss());
    toastPromise.then((toast) => {
      toast.onDidDismiss().then(() => {
        if (this.activeSceneToast === toastPromise) { this.activeSceneToast = undefined; }
      });
      toast.present();
    });
  }

  /** Press-and-hold a scene tile opens its editor. */
  onPressStart(scene: SceneDef): void {
    if (this.editMode) { return; }              // reordering — long-press is inert
    this.longPressed = false;
    if (this.pressTimer) { clearTimeout(this.pressTimer); }
    this.pressTimer = setTimeout(() => {
      this.pressTimer = null;
      this.longPressed = true;
      // This finger owns the carousel (its pointerdown bubbled to the viewport) and is still down,
      // but navigating away means its pointerup lands on the editor, not here. Release now, or the
      // carousel comes back from the editor still owned by a finger that can never let go.
      this.clearSwipe();
      this.openEditor(scene);
    }, this.LONG_PRESS_MS);
  }

  onPressEnd(): void {
    if (this.pressTimer) { clearTimeout(this.pressTimer); this.pressTimer = null; }
  }

  /** Clear the one-shot bounce class once its animation finishes. */
  onBounceEnd(): void {
    this.bouncingId = null;
    this.cdr.markForCheck();
  }

  openEditor(scene?: SceneDef): void {
    this.router.navigate(scene ? ['/scenes/edit', scene.id] : ['/scenes/new']);
  }

  // --- Reorder mode: drag a tile to a new slot ---
  toggleEdit(): void {
    if (this.editMode) { this.exitEditMode(); return; }
    this.editMode = true;
    this.cdr.markForCheck();
  }

  /** The one way out of reorder mode: persist the final order once, then clear the state.
   *  Reached from Done and from collapsing the panel. (Teardown persists separately in
   *  ngOnDestroy — it must not touch change detection on a destroyed view.) */
  private exitEditMode(): void {
    // End any drag FIRST, through the normal path: it releases the pressed tile's pointer capture,
    // detaches the move listener, and commits the move the neighbours had already slid aside for —
    // dropping that on the floor would contradict what the resident was looking at. It also has to
    // happen before editMode flips, because onDragEnd is what marks the trailing click to be
    // swallowed, and onCardTap's editMode guard is gone the moment the mode ends.
    this.onDragEnd();
    if (this.reorderDirty) { this.persistOrder(); }
    this.editMode = false;
    this.cdr.markForCheck();
  }

  /** Persist the current order, clearing the dirty flag only once the write actually lands.
   *
   *  Note reorderScenes REPORTS failure rather than throwing it: FeatureSettingsService
   *  .updateResidentSettings catches everything and returns false, so an offline or rejected
   *  write resolves `false` and never reaches a .catch. Treating only rejections as failure
   *  left the flag cleared on a lost write — no retry, no word to the resident.
   *
   *  `silent` is for teardown, where there is no UI left to show a toast on. */
  private persistOrder(silent = false): void {
    const revision = this.orderRevision;   // the order this particular write carries
    this.orderWriteInFlight = true;
    this.scenesService.reorderScenes(this.scenes.map(s => s.id))
      .then(saved => {
        this.orderWriteInFlight = false;
        // A newer move happened while this was in flight — it owns the dirty flag now, and
        // clearing it here would silently drop the resident's most recent reorder.
        if (revision !== this.orderRevision) { return; }
        if (saved) { this.reorderDirty = false; return; }
        this.onOrderWriteFailed(silent);
      })
      .catch(() => {
        this.orderWriteInFlight = false;
        if (revision === this.orderRevision) { this.onOrderWriteFailed(silent); }
      });
  }

  private onOrderWriteFailed(silent: boolean): void {
    this.reorderDirty = true;   // stays pending so teardown gets one more attempt
    if (!silent && !this.destroyed) {
      this.presentSceneToast("Couldn't save the new order. Try again in a moment.");
    }
  }

  /** Collapsing the widget while reordering commits and leaves reorder mode — otherwise the
   *  collapsed view stays in a state it can't show, where tiles pick instead of firing. */
  onPanelToggle(expanded: boolean): void {
    if (!expanded && this.editMode) { this.exitEditMode(); }
  }

  /** Forget an in-flight swipe: no owner, no offset, the track's transition back on. The single
   *  place swipe ownership is released, so it cannot be released down one path and not another. */
  private clearSwipe(): void {
    this.swipePointerId = null;
    this.swipeActive = false;
    this.swiping = false;
    this.dragDx = 0;
    this.cdr.markForCheck();
  }

  // --- Drag-to-reorder ---
  //
  // Hand-rolled rather than CDK: its drop lists sort along one axis only (grid-aware 'mixed'
  // orientation arrived in CDK 17, this app is on 16), so it cannot sort a wrapped 4-across grid.
  // The arithmetic lives in scene-grid-drag.ts; this part is just pointer plumbing.
  //
  // Nothing moves in `scenes` until the drop: during the drag the DOM order is frozen and tiles are
  // displaced with transforms. That keeps the tiles' measured geometry valid for the whole gesture
  // and means an abandoned drag needs no undo.

  /** pointerdown on a tile: remember it, but don't start dragging until the finger actually moves.
   *
   *  Only ONE pointer may drive the gesture. A second finger landing mid-drag used to overwrite the
   *  candidate, so the held tile jumped to the new finger's anchor and the tiles around THAT finger
   *  slid aside — while the tile actually being dragged was somewhere else entirely. Extra pointers
   *  are ignored outright until the owning one releases. */
  onDragStart(scene: SceneDef, index: number, ev: PointerEvent): void {
    if (!this.editMode) { return; }
    if (this.dragPointerId !== null) { return; }        // another finger already owns this gesture
    const el = ev.currentTarget as HTMLElement;
    // Capture so the gesture keeps reporting once the finger leaves the tile it started on.
    try { el.setPointerCapture(ev.pointerId); } catch { /* unsupported in some test/DOM shims */ }
    this.dragPointerId = ev.pointerId;
    this.dragCandidate = { id: scene.id, index, x: ev.clientX, y: ev.clientY, el };
    // Moves are followed only for the life of this gesture — see dragMoveListener.
    this.dragMoveListener = (move: PointerEvent) => this.onDragMove(move);
    el.addEventListener('pointermove', this.dragMoveListener);
  }

  onDragMove(ev: PointerEvent): void {
    if (ev.pointerId !== this.dragPointerId) { return; }   // not the finger that owns the gesture
    const candidate = this.dragCandidate;
    if (!candidate) { return; }
    const dx = ev.clientX - candidate.x;
    const dy = ev.clientY - candidate.y;

    if (!this.dragId) {
      if (Math.abs(dx) < this.DRAG_START_PX && Math.abs(dy) < this.DRAG_START_PX) { return; }
      const measured = this.measureGrid();
      // Nothing measurable — abandon the gesture through onDragEnd so the capture and the move
      // listener are released rather than left dangling on the tile.
      if (!measured) { this.onDragEnd(); return; }
      this.dragMetrics = measured.metrics;
      this.dragBox = measured.box;
      this.dragId = candidate.id;
      this.dragFrom = candidate.index;
    }
    const metrics = this.dragMetrics;
    const box = this.dragBox;
    if (!metrics || !box) { return; }

    // Follow the finger, but never out of the grid.
    const held = clampOffsetToGrid(dx, dy, this.dragFrom, metrics, box);
    this.dragOffsetX = held.dx;
    this.dragOffsetY = held.dy;
    // Grid-relative coordinates, re-read each move: if the page scrolls under the finger, the
    // container rect and the pointer shift together and the hit test stays honest.
    const origin = this.sceneGrid?.nativeElement.getBoundingClientRect();
    this.dragTo = targetIndexFromPoint(
      ev.clientX - (origin?.left ?? 0), ev.clientY - (origin?.top ?? 0), metrics, this.scenes.length,
    );
    this.cdr.markForCheck();
  }

  /** Drop: commit the new order, exactly once.
   *
   *  Reached from pointerup, pointercancel AND lostpointercapture, and releasePointerCapture below
   *  can itself fire lostpointercapture — so the state is read and cleared BEFORE anything else
   *  happens. A re-entrant call then finds no held tile and returns, instead of committing the
   *  same move a second time.
   *
   *  `ev` is absent only when the component ends the drag itself (leaving reorder mode). A pointer
   *  that does not own the gesture must not end it — otherwise lifting a stray second finger
   *  committed the first finger's half-finished drag. */
  onDragEnd(ev?: PointerEvent): void {
    if (ev && ev.pointerId !== this.dragPointerId) { return; }
    const candidate = this.dragCandidate;
    const pointerId = this.dragPointerId;
    const held = this.dragId;
    const from = this.dragFrom;
    const to = this.dragTo;
    this.clearDrag();

    if (candidate && pointerId !== null) {
      try { candidate.el.releasePointerCapture(pointerId); } catch { /* already released */ }
    }
    if (!held) { return; }               // a stationary tap in reorder mode — nothing to commit
    // Mark the trailing click as spent before anything can flip editMode out from under onCardTap.
    this.dragEndedAt = Date.now();
    if (to >= 0 && to !== from) { this.moveScene(from, to); }
    this.cdr.markForCheck();
  }

  private clearDrag(): void {
    if (this.dragCandidate && this.dragMoveListener) {
      this.dragCandidate.el.removeEventListener('pointermove', this.dragMoveListener);
    }
    this.dragMoveListener = null;
    this.dragCandidate = null;
    this.dragPointerId = null;
    this.dragId = null;
    this.dragFrom = -1;
    this.dragTo = -1;
    this.dragOffsetX = 0;
    this.dragOffsetY = 0;
    this.dragMetrics = null;
    this.dragBox = null;
  }

  /** Measure the tiles and the grid box once, grid-relative, so a mid-drag scroll can't skew either. */
  private measureGrid(): { metrics: GridMetrics; box: GridBox } | null {
    const host = this.sceneGrid?.nativeElement;
    if (!host) { return null; }
    const base = host.getBoundingClientRect();
    const rects: TileRect[] = Array.from(host.querySelectorAll<HTMLElement>('.scene-tile')).map(el => {
      const r = el.getBoundingClientRect();
      // Size comes from offsetWidth/Height, which ignore transforms. A drag always starts inside
      // reorder mode, where the tiles are mid-rotation under the idle wiggle, and a rect would
      // report the ROTATED bounding box — a couple of pixels too big, which would then eat into the
      // clamp bounds and stop the held tile short of the grid edge. Positions still come from the
      // rect (offsetLeft is relative to the offsetParent, not this grid); rotation shifts those by
      // well under a pixel at ±1.2°, and cancels between tiles.
      return { left: r.left - base.left, top: r.top - base.top, width: el.offsetWidth, height: el.offsetHeight };
    });
    const metrics = gridMetrics(rects);
    return metrics ? { metrics, box: { width: base.width, height: base.height } } : null;
  }

  /** Mid-drag transform: the held tile follows the finger, the others slide aside to open the gap. */
  tileTransform(index: number, scene: SceneDef): string | null {
    if (!this.dragId || !this.dragMetrics) { return null; }
    if (scene.id === this.dragId) {
      return `translate(${this.dragOffsetX}px, ${this.dragOffsetY}px) scale(1.08)`;
    }
    const { dx, dy } = displacementFor(index, this.dragFrom, this.dragTo, this.dragMetrics);
    return (dx || dy) ? `translate(${dx}px, ${dy}px)` : null;
  }

  /** Move the scene in slot `from` to slot `to`. The drag already knows both, so it passes indices
   *  straight in rather than round-tripping them through an id for this to look up again. */
  private moveScene(from: number, to: number): void {
    if (from < 0 || to < 0 || from >= this.scenes.length || to >= this.scenes.length || from === to) { return; }
    const arr = [...this.scenes];              // new array: OnPush-friendly + never mutate a shared ref
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    this.scenes = arr;
    this.rebuildPages();
    this.reorderDirty = true;                  // local-only during editing; persisted once on Done
    this.orderRevision++;                      // supersedes any write still in flight
  }

  // --- Pagination (collapsed view): step the carets between pages of PAGE_SIZE tiles ---
  prevPage(): void { if (this.page > 0) { this.page--; this.cdr.markForCheck(); } }
  nextPage(): void { if (this.page < this.pages.length - 1) { this.page++; this.cdr.markForCheck(); } }

  // --- Carousel swipe: drag the viewport horizontally to change page; snaps on release ---

  /** pointerdown in the carousel: anchor the swipe on this finger.
   *
   *  Only ONE pointer may drive it. A second finger landing mid-swipe used to re-anchor the gesture
   *  on itself and zero the offset with `swiping` back to false, so the track animated back to the
   *  page origin and both fingers' moves were then measured from that one anchor — the carousel
   *  stuttered and fought the finger. Extra pointers are ignored until the owning one releases. */
  onSwipeStart(ev: PointerEvent): void {
    if (this.editMode) { return; }              // no paging while reordering
    if (this.swipePointerId !== null) {
      if (!ev.isPrimary) {
        // A genuine extra finger: never primary while another pointer of its type is down.
        // The tile it landed on has already armed the long-press (a tile's pointerdown runs before
        // it bubbles up here) — and pressTimer is shared, so it just replaced the owning finger's
        // hold with its own. A pointer the carousel refuses to track must not open an editor
        // either: drop the hold outright rather than open the wrong tile's.
        this.onPressEnd();
        return;
      }
      // Primary means nothing else is touching, so the owner we are still holding is a ghost: its
      // release never reached us. That happens whenever the gesture ends somewhere other than this
      // element — the collapsed view hidden mid-swipe, a navigation, a dropped touch sequence — and
      // without this the carousel would ignore every finger for the rest of the session. Verified
      // on device: a sequence that ended without pointerup left swipePointerId claimed for good.
      this.clearSwipe();
    }
    this.swipePointerId = ev.pointerId;
    this.swipeActive = true;
    this.swiping = false;
    this.swiped = false;
    this.swipeStartX = ev.clientX;
    this.swipeStartY = ev.clientY;
    this.dragDx = 0;
  }

  onSwipeMove(ev: PointerEvent): void {
    if (!this.swipeActive) { return; }
    if (ev.pointerId !== this.swipePointerId) { return; }   // not the finger that owns the swipe
    const dx = ev.clientX - this.swipeStartX;
    const dy = ev.clientY - this.swipeStartY;
    if (!this.swiping) {
      // Vertical intent → let the page scroll; give up this gesture.
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) { this.swipeActive = false; return; }
      if (Math.abs(dx) <= 8) { return; }
      this.swiping = true;
      this.onPressEnd();               // a swipe is not a long-press
    }
    // Rubber-band resistance when dragging past the first/last page.
    const atStart = this.page === 0;
    const atEnd = this.page >= this.pages.length - 1;
    this.dragDx = ((atStart && dx > 0) || (atEnd && dx < 0)) ? dx / 3 : dx;
    this.cdr.markForCheck();
  }

  /** Lift: only the finger that started the swipe may end it — otherwise lifting a stray second
   *  finger paged on ITS travel, or cancelled a swipe still under way. */
  onSwipeEnd(ev: PointerEvent): void {
    if (ev.pointerId !== this.swipePointerId) { return; }
    const committing = this.swipeActive && this.swiping;   // a tap, or a gesture given up to a
    const dx = ev.clientX - this.swipeStartX;              // vertical scroll, pages nothing
    this.clearSwipe();                                     // released whether it pages or not
    if (!committing) { return; }
    if (dx <= -this.SWIPE_COMMIT_PX) { this.nextPage(); }
    else if (dx >= this.SWIPE_COMMIT_PX) { this.prevPage(); }
    this.swiped = true;                // suppress the tile click that follows a drag
    this.cdr.markForCheck();
  }

  /** The swipe was taken away rather than lifted: the browser claimed the gesture (pointercancel,
   *  e.g. it decided the finger is scrolling the page), or the tile holding the implicit touch
   *  capture stopped being rendered (lostpointercapture — the panel toggled, or a long-press
   *  navigated to the editor). Either way the travel is not a decision to page — a diagonal drag
   *  that the compositor turned into a vertical scroll used to page the carousel on the way out —
   *  so discard it, but still swallow the click that may trail it, and always release ownership. */
  onSwipeAbort(ev: PointerEvent): void {
    if (ev.pointerId !== this.swipePointerId) { return; }
    if (this.swiping) { this.swiped = true; }
    this.clearSwipe();
  }

  readonly trackPage = (i: number): number => i;
  readonly trackTile = (_: number, t: { scene?: SceneDef }): string => t.scene?.id ?? '__new__';
  // Track grid tiles by id so a reorder MOVES the existing DOM node instead of destroying and
  // recreating it (which flickered/"bounced" when the settings stream re-emitted new object refs).
  readonly trackScene = (_: number, s: SceneDef): string => s.id;

  private rebuildPages(): void {
    // All scene tiles followed by the "New" tile ({} = New), chunked into pages of PAGE_SIZE.
    const tiles: Array<{ scene?: SceneDef }> = [...this.scenes.map(s => ({ scene: s })), {}];
    const pages: Array<{ scene?: SceneDef }>[] = [];
    for (let i = 0; i < tiles.length; i += this.PAGE_SIZE) { pages.push(tiles.slice(i, i + this.PAGE_SIZE)); }
    this.pages = pages;
    if (this.page > pages.length - 1) { this.page = Math.max(0, pages.length - 1); }
  }

  private ensureReady(): boolean {
    if (this.controlAvailability === 'ready') { return true; }
    this.presentSceneToast(this.controlAvailability === 'needs_reauth'
      ? 'Please sign in again to control your home.'
      : 'Reconnecting to your home. Try again in a moment.');
    return false;
  }

  override ngOnDestroy(): void {
    // Idempotent: some specs call ngOnDestroy() explicitly, then TestBed auto-destroys the
    // fixture — and CommonPage.ngOnDestroy is not safe to run twice (destroy$.next() after
    // unsubscribe() throws). Guard it.
    if (this.destroyed) { return; }
    this.destroyed = true;
    // Last chance to persist an unsaved reorder. Skipped when a write is already in flight —
    // that one is still going to land, and firing another here would double-write every reorder
    // where the resident navigates away before the round trip finishes.
    if (this.reorderDirty && !this.orderWriteInFlight) { this.persistOrder(true); }
    if (this.pressTimer) { clearTimeout(this.pressTimer); this.pressTimer = null; }
    this.activeSceneToast?.then((toast) => toast.dismiss());   // don't leave a toast up after teardown
    this.widgetDestroy$.next(); this.widgetDestroy$.complete();
    super.ngOnDestroy();
  }
}
